import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { loungeCopy } from "@/lib/loungeCopy";
import { getLoungeProfile } from "@/lib/loungeTrack";

const EMPTY = { name: "", email: "", organization: "", subject: "", message: "", website: "" };

/** «Contattaci / Scrivi all'azienda» delle Lounge pubbliche: messaggio libero salvato in piattaforma + notifica Marketing. */
export function LoungeContactButton({ area = "press", lang = "it", compact = false, variant = "header", testidPrefix = "lounge-contact" }) {
  const c = loungeCopy(lang);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    const p = getLoungeProfile(area) || {};
    setForm((f) => ({ ...f, name: f.name || p.name || "", email: f.email || p.email || "", organization: f.organization || p.outlet || p.social || "" }));
  }, [open, area]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) { toast.error(c.contact_required); return; }
    setSending(true);
    try {
      let ref = "";
      try { ref = sessionStorage.getItem("lounge_ref") || ""; } catch { /* ignore */ }
      const payload = {
        name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim(),
        website: form.website, ref, lang, source_page: area,
        ...(area === "ambassador" ? { social: form.organization.trim() } : { organization: form.organization.trim() }),
      };
      await api.post("/public/lounge/contact", payload);
      toast.success(c.contact_ok);
      setForm((f) => ({ ...f, subject: "", message: "" }));
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || c.error);
    } finally { setSending(false); }
  };

  const btnCls = variant === "footer"
    ? "inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    : "h-9 rounded-full border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-brema-hover sm:px-4";

  return (
    <>
      {variant === "footer" ? (
        <button type="button" onClick={() => setOpen(true)} className={btnCls} data-testid={`${testidPrefix}-btn`}>
          <MessageSquare className="h-3.5 w-3.5" /> {c.contact_btn}
        </button>
      ) : (
        <Button type="button" variant="outline" onClick={() => setOpen(true)} className={btnCls} data-testid={`${testidPrefix}-btn`} title={c.contact_btn} aria-label={c.contact_btn}>
          <MessageSquare className="h-4 w-4" />
          <span className={compact ? "hidden sm:inline" : ""}>{c.contact_btn}</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testidPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-brand" /> {c.contact_title}</DialogTitle>
            <DialogDescription>{area === "ambassador" ? c.contact_sub_ambassador : c.contact_sub_press}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid={`${testidPrefix}-form`}>
            <input type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{c.f_name} <span className="text-brand">*</span></Label>
                <Input value={form.name} onChange={set("name")} data-testid={`${testidPrefix}-name`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{c.f_email} <span className="text-brand">*</span></Label>
                <Input type="email" value={form.email} onChange={set("email")} data-testid={`${testidPrefix}-email`} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">{area === "ambassador" ? c.f_social : c.f_outlet}</Label>
                <Input value={form.organization} onChange={set("organization")} data-testid={`${testidPrefix}-org`} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">{c.f_subject}</Label>
                <Input value={form.subject} onChange={set("subject")} maxLength={200} data-testid={`${testidPrefix}-subject`} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{c.f_message} <span className="text-brand">*</span></Label>
              <Textarea rows={5} value={form.message} onChange={set("message")} maxLength={5000} data-testid={`${testidPrefix}-message`} />
            </div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90" data-testid={`${testidPrefix}-submit`}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {sending ? c.sending : c.contact_send}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
